import { type ReactNode, useMemo, useState } from "react";
import { BaseButton } from "../../../components/Button/BaseButton.tsx";
import { Button } from "../../../components/Button/Button.tsx";
import { Progress } from "../../../components/Progress/Progress.tsx";
import { Spinner } from "../../../components/Spinner/Spinner.tsx";
import { computeDateDifference, formatDate } from "../../../utils/date.ts";
import { upgradeDialogStore } from "../../components/UpgradeDialog/upgradeDialogStore.ts";
import { CREDIT_PRICE } from "../../constants.ts";
import { useWorkspace } from "../../hooks/useWorkspace.ts";
import { useWorkspaceDetails } from "../../hooks/useWorkspaceDetails.ts";
import type { Credit } from "../../types.ts";
import { useCreateCustomerPortalUrl } from "../HomeIndex/hooks/useCreateCustomerPortalUrl.ts";
import { SettingsContentLayout } from "./components/SettingsContentLayout.tsx";
import { profileSettingsDialogStore } from "./stores/profileSettingsDialogStore.ts";
export const PlanAndBillingSettings = () => {
  const { workspace } = useWorkspace();

  return (
    <SettingsContentLayout
      header={
        <div className="flex-col">
          <div className="label-lg-semibold text-primary">Plan & billing</div>
          <div className="label-md-default text-secondary">
            Monitor your workspace usage, manage your plan, limits and payments
          </div>
        </div>
      }
      items={[
        workspace.type === "premium" ? (
          <PremiumPlanDetails />
        ) : workspace.type === "pro" ? (
          <ProPlanDetails />
        ) : workspace.type === "team" ? (
          <TeamPlanDetails />
        ) : workspace.type === "enterprise" ? (
          <EnterprisePlanDetails />
        ) : (
          <FreePlanDetails />
        ),
      ]}
    />
  );
};

const FreePlanDetails = () => (
  <PlanDetailsLayout
    header={
      <div className="flex-row w-full justify-between">
        <div className="heading-2xl text-primary">Free</div>
        <div className="flex-row gap-200">
          <UpgradePlanButton />
        </div>
      </div>
    }
  />
);

const ProPlanDetails = () => (
  <PlanDetailsLayout
    header={
      <div className="flex-row w-full justify-between">
        <div className="heading-2xl text-primary">Pro</div>
        <div className="flex-row gap-200">
          <CancelPlanButton />
          <ManageBillingButton />
          <UpgradePlanButton />
        </div>
      </div>
    }
  />
);

const PremiumPlanDetails = () => (
  <PlanDetailsLayout
    header={
      <div className="flex-row w-full justify-between">
        <div className="heading-2xl text-primary">Premium</div>
        <div className="flex-row gap-200">
          <CancelPlanButton />
          <ManageBillingButton />
          <UpgradePlanButton />
        </div>
      </div>
    }
  />
);

const TeamPlanDetails = () => (
  <PlanDetailsLayout
    header={
      <div className="flex-row w-full justify-between">
        <div className="heading-2xl text-primary">Team</div>
        <div className="flex-row gap-200">
          <CancelPlanButton />
          <ManageBillingButton />
          <UpgradePlanButton />
        </div>
      </div>
    }
  />
);

const EnterprisePlanDetails = () => (
  <PlanDetailsLayout
    header={
      <div className="flex-row w-full justify-between">
        <div className="heading-2xl text-primary">Enterprise</div>
        <div className="flex-row gap-200">
          <CancelPlanButton />
          <ManageBillingButton />
        </div>
      </div>
    }
  />
);

const PlanDetailsLayout = ({ header }: { header: ReactNode }) => {
  const { details, isLoading } = useWorkspaceDetails({});
  const { workspace } = useWorkspace();
  const { createCustomerPortalUrl, isCreateCustomerPortalUrlLoading } =
    useCreateCustomerPortalUrl();

  const { totalCredits, remainingCredits } = useMemo(
    () => ({
      totalCredits: details?.credits.sum((it) => it.original_amount),
      remainingCredits: details?.credits.sum((it) => it.remaining_amount),
    }),
    [details?.credits],
  );

  return isLoading || !details ? (
    <div className="flex-col flex-fill h-full items-center justify-center">
      <Spinner />
    </div>
  ) : (
    <div className="flex-col flex-fill overflow-y-auto gap-600 scrollbar-hide">
      <div className="flex-col rounded-200 border divide-y-025 divide-primary-rest">
        <div className="flex-row items-center p-600">
          <div className="flex-row w-full items-center h-1000">{header}</div>
        </div>
        <PlanDetailRow
          title="Credits"
          planDetails={
            <div className="flex-col gap-600">
              <div className="flex-col gap-300">
                {totalCredits !== undefined &&
                  remainingCredits !== undefined && (
                    <PlanDetailItem
                      title={
                        <div className="label-lg-semibold text-primary">
                          {totalCredits} Included workspace credits
                        </div>
                      }
                      subtitle={
                        <div className="label-md-default text-secondary">
                          Credits are shared between workspace members
                        </div>
                      }
                      totalAmount={totalCredits}
                      usedAmount={totalCredits - remainingCredits}
                    />
                  )}
                <CreditDetailsSection credits={details.credits} />
              </div>
              {details.additional_credits_consumed !== null && (
                <>
                  <div className="w-full h-[1px] bg-surface-primary-active" />
                  <PlanDetailItem
                    title={
                      <div className="label-lg-semibold text-primary">
                        Additional credits
                      </div>
                    }
                    subtitle={
                      <div className="flex-col gap-100">
                        <div className="label-md-default text-secondary">
                          Debited after all your workspace credits are consumed
                        </div>
                        <div className="label-md-default text-secondary">
                          With your current subscription, each additional credit
                          costs approximately $
                          {(CREDIT_PRICE[workspace.type] * 0.01).toFixed(2)}
                        </div>
                        <div className="flex-row justify-start">
                          <BaseButton
                            className="underline label-md-default text-secondary"
                            onClick={() => {
                              window.open(
                                "https://help.pimento.design/en/articles/11107656-how-additional-credits-work",
                                "_blank",
                              );
                            }}
                          >
                            Learn more about additional credits
                          </BaseButton>
                        </div>
                      </div>
                    }
                    usedAmount={details.additional_credits_consumed}
                  />
                </>
              )}
            </div>
          }
        />
        {/* FIXME: We must add pay as you go credits */}
        {/* FIXME: We should adapt number of seats per workspace type */}
        <PlanDetailRow
          title="Members"
          planDetails={
            <PlanDetailItem
              title={
                <div className="label-lg-semibold text-primary">
                  {details.num_seats} Seat
                  {details.num_seats && details.num_seats > 1 ? "s" : ""}
                </div>
              }
              usedAmount={details.num_members}
              totalAmount={details.num_seats}
              extra={
                <Button
                  size="sm"
                  variant="secondary"
                  className="!px-0"
                  onClick={() =>
                    profileSettingsDialogStore.setProfileSettingsTab("members")
                  }
                >
                  Manage members
                </Button>
              }
            />
          }
        />
      </div>
      <div className="flex-col gap-400" />
      <div className="">Invoices</div>
      <div className="w-full h-025 border-t" />
      <div className="flex-row">
        <Button
          size="md"
          variant="secondary"
          iconName="ExternalLink"
          loading={isCreateCustomerPortalUrlLoading}
          onClick={() => {
            createCustomerPortalUrl({ workspace_uuid: workspace.uuid });
          }}
        >
          View invoices
        </Button>
      </div>
    </div>
  );
};

const CreditDetailsSection = ({ credits }: { credits: Credit[] }) => {
  const [isUnfolded, setIsUnfolded] = useState(false);

  return (
    <div className="flex-col-center gap-300">
      {isUnfolded && (
        <div className="w-full pl-400 flex-col gap-300">
          {credits.map((credit, index) => (
            <CreditDetailRow key={index} credit={credit} />
          ))}
        </div>
      )}
      <Button
        size="sm"
        variant="tertiary"
        className="w-[120px]"
        onClick={() => setIsUnfolded((unfolded) => !unfolded)}
      >
        {isUnfolded ? "Hide details" : "Show details"}
      </Button>
    </div>
  );
};

const CreditDetailRow = ({ credit }: { credit: Credit }) => {
  switch (credit.origin) {
    case "INCLUDED_WITH_FIRST_WORKSPACE":
      return (
        <PlanDetailItem
          title={
            <div className="label-md-semibold text-primary">
              {credit.original_amount} Free credits
            </div>
          }
          subtitle={
            <div className="label-md-default text-secondary">
              Valid until{" "}
              <span className="label-md-semibold text-primary">
                {formatDate(credit.valid_until)}
              </span>
            </div>
          }
          totalAmount={credit.original_amount}
          usedAmount={credit.original_amount - credit.remaining_amount}
        />
      );
    case "MANUAL":
      return (
        <PlanDetailItem
          title={
            <div className="label-md-semibold text-primary">
              {credit.original_amount} Free credits
            </div>
          }
          subtitle={
            <div className="label-md-default text-secondary">
              Valid until{" "}
              <span className="label-md-semibold text-primary">
                {formatDate(credit.valid_until)}
              </span>
            </div>
          }
          totalAmount={credit.original_amount}
          usedAmount={credit.original_amount - credit.remaining_amount}
        />
      );
    case "TOP_UP":
      return (
        <PlanDetailItem
          title={
            <div className="label-md-semibold text-primary">
              {credit.original_amount} Purchased credits
            </div>
          }
          subtitle={
            <div className="label-md-default text-secondary">
              Valid until{" "}
              <span className="label-md-semibold text-primary">
                {formatDate(credit.valid_until)}
              </span>
            </div>
          }
          totalAmount={credit.original_amount}
          usedAmount={credit.original_amount - credit.remaining_amount}
        />
      );

    case "SUBSCRIPTION":
      return (
        <PlanDetailItem
          title={
            <div className="label-md-semibold text-primary">
              {credit.original_amount}
              {/* FIXME: Currently we decide whether a credit is monthly or yearly based on dates difference using a 6 months threshold
               * We should use the billing period from Stripe instead and store it in DB during credit creation
               */}
              {computeDateDifference(
                new Date(credit.valid_from),
                new Date(credit.valid_until),
              ) >
              60 * 60 * 24 * 30 * 6
                ? " Yearly "
                : " Monthly "}
              Plan credits
            </div>
          }
          subtitle={
            <div className="label-md-default text-secondary">
              Valid until{" "}
              <span className="label-md-semibold text-primary">
                {formatDate(credit.valid_until)}
              </span>
            </div>
          }
          totalAmount={credit.original_amount}
          usedAmount={credit.original_amount - credit.remaining_amount}
        />
      );
  }
};

const CancelPlanButton = () => {
  const { createCustomerPortalUrl, isCreateCustomerPortalUrlLoading } =
    useCreateCustomerPortalUrl();
  const { workspace } = useWorkspace();

  return (
    <Button
      size="md"
      variant="tertiary"
      loading={isCreateCustomerPortalUrlLoading}
      onClick={() => {
        workspace.type === "enterprise"
          ? window.open("mailto:contact@pimento.design", "_blank")
          : createCustomerPortalUrl({
              workspace_uuid: workspace.uuid,
            });
      }}
    >
      Cancel plan
    </Button>
  );
};

const ManageBillingButton = () => {
  const { workspace } = useWorkspace();
  const { createCustomerPortalUrl, isCreateCustomerPortalUrlLoading } =
    useCreateCustomerPortalUrl();

  return (
    <Button
      size="md"
      variant="secondary"
      loading={isCreateCustomerPortalUrlLoading}
      onClick={() => {
        createCustomerPortalUrl({
          workspace_uuid: workspace.uuid,
        });
      }}
    >
      Manage billing
    </Button>
  );
};

const UpgradePlanButton = () => (
  <Button
    size="md"
    variant="primary"
    iconName="Sparkles"
    onClick={() => {
      upgradeDialogStore.openDialog();
    }}
  >
    Upgrade plan
  </Button>
);

const PlanDetailRow = ({
  title,
  planDetails,
}: {
  title: string;
  planDetails: ReactNode;
}) => (
  <div className="flex-row p-600 gap-200">
    <div className="w-[120px] label-md-semibold text-disabled">{title}</div>
    <div className="flex-col flex-fill">{planDetails}</div>
  </div>
);

const PlanDetailItem = ({
  title,
  subtitle,
  usedAmount,
  totalAmount,
  extra,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  usedAmount: number;
  totalAmount?: number;
  extra?: ReactNode;
}) => (
  <div className="flex-row">
    <div className="flex-row flex-fill">
      <div className="flex-col">
        {title}
        {subtitle}
      </div>
      <div />
    </div>
    {totalAmount !== undefined ? (
      <div className="flex-col min-w-[120px] gap-300">
        <div className="flex-col-center h-200">
          <Progress value={usedAmount / totalAmount} />
        </div>
        <div className="flex-row justify-end label-md-default text-secondary">
          {usedAmount}/{totalAmount} Used
        </div>
        {extra}
      </div>
    ) : (
      <div className="flex-col min-w-[120px] gap-300">
        <div className="flex-row justify-end label-md-default text-primary">
          {usedAmount} Used
        </div>
        {extra}
      </div>
    )}
  </div>
);
